<template>
  <transition name="message-fade">
    <div v-if="visible" :class="messageClass" class="message">
      <div class="message-content">
        <i :class="iconClass" class="message-icon"></i>
        <span class="message-text">{{ message }}</span>
        <div v-if="showActions" class="message-actions">
          <button 
            v-for="(action, index) in actions" 
            :key="index"
            @click="handleAction(action)"
            :class="['message-btn', action.type || 'default']"
          >
            {{ action.text }}
          </button>
        </div>
        <i v-if="showClose" class="message-close" @click="close">×</i>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'MessageComponent',
  data() {
    return {
      visible: false,
      message: '',
      type: 'info',
      duration: 3000,
      timer: null,
      actions: [],
      showClose: true,
      onClose: null
    }
  },
  computed: {
    messageClass() {
      return {
        'message-success': this.type === 'success',
        'message-error': this.type === 'error',
        'message-warning': this.type === 'warning',
        'message-info': this.type === 'info'
      }
    },
    iconClass() {
      const iconMap = {
        success: 'icon-success',
        error: 'icon-error',
        warning: 'icon-warning',
        info: 'icon-info'
      }
      return iconMap[this.type] || 'icon-info'
    },
    showActions() {
      return this.actions && this.actions.length > 0
    }
  },
  methods: {
    show(options) {
      if (typeof options === 'string') {
        this.message = options
        this.type = 'info'
        this.duration = 3000
        this.actions = []
        this.showClose = true
        this.onClose = null
      } else {
        this.message = options.message || ''
        this.type = options.type || 'info'
        this.duration = options.duration !== undefined ? options.duration : 3000
        this.actions = options.actions || []
        this.showClose = options.showClose !== undefined ? options.showClose : true
        this.onClose = options.onClose || null
      }
      
      this.visible = true
      
      if (this.timer) {
        clearTimeout(this.timer)
      }
      
      if (this.duration > 0) {
        this.timer = setTimeout(() => {
          this.close()
        }, this.duration)
      }
    },
    handleAction(action) {
      if (action.handler && typeof action.handler === 'function') {
        action.handler()
      }
      if (action.closeOnClick !== false) {
        this.close()
      }
    },
    close() {
      this.visible = false
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      if (this.onClose && typeof this.onClose === 'function') {
        this.onClose()
      }
    }
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
  }
}
</script>

<style scoped>
.message {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  min-width: 300px;
  max-width: 500px;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.message-content {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-left: 4px solid #409eff;
  flex-wrap: wrap;
}

.message-icon {
  margin-right: 8px;
  font-size: 16px;
  font-style: normal;
}

.message-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.4;
  color: #606266;
  min-width: 0;
}

.message-actions {
  display: flex;
  gap: 8px;
  margin-left: 8px;
  flex-shrink: 0;
}

.message-btn {
  padding: 4px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fff;
  color: #606266;
}

.message-btn:hover {
  background: #f5f7fa;
}

.message-btn.primary {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

.message-btn.primary:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.message-btn.danger {
  background: #f56c6c;
  border-color: #f56c6c;
  color: #fff;
}

.message-btn.danger:hover {
  background: #f78989;
  border-color: #f78989;
}

.message-close {
  margin-left: 8px;
  font-size: 18px;
  color: #909399;
  cursor: pointer;
  font-style: normal;
  line-height: 1;
  flex-shrink: 0;
}

.message-close:hover {
  color: #606266;
}

/* 不同类型的样式 */
.message-success .message-content {
  border-left-color: #67c23a;
}

.message-success .message-icon::before {
  content: '✓';
  color: #67c23a;
  font-weight: bold;
}

.message-error .message-content {
  border-left-color: #f56c6c;
}

.message-error .message-icon::before {
  content: '✕';
  color: #f56c6c;
  font-weight: bold;
}

.message-warning .message-content {
  border-left-color: #e6a23c;
}

.message-warning .message-icon::before {
  content: '⚠';
  color: #e6a23c;
  font-weight: bold;
}

.message-info .message-content {
  border-left-color: #409eff;
}

.message-info .message-icon::before {
  content: 'ℹ';
  color: #409eff;
  font-weight: bold;
}

/* 动画效果 */
.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}

.message-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-20px);
}
</style>